import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  CONSOLIDATE_DEFAULTS,
  consolidateDiscoveredDuplicates,
  deleteDiscoveredOldRows,
  discoveredDuplicatesQuery,
  discoveredOldRowsQuery,
  setDiscoveredAutoConsolidate,
  type ConsolidateOptions,
  type ConsolidateScope,
  type DiscoveredDuplicates,
  type DuplicateGroup,
  type DuplicateRow,
} from '../../api/discoveredActions'
import { Modal } from '../../ui/Modal'
import './ConsolidateDuplicatesModal.css'
import { toast } from '../../ui/notify'

/**
 * Fereastra "Consolidate duplicates" din Live Devices > Discovered.
 *
 * Doua actiuni separate, pe doua file, fiecare cu butonul ei:
 *  - "Merge duplicates": cum se strang dublurile (implicit releele aceluiasi
 *    sediu, cele fara MAC in randul cu MAC), planul in "Advanced". Acolo omul
 *    alege, pe fiecare grup, randul care ramane (Keep) si poate debifa un rand
 *    anume (Include) - randul debifat ramane neatins. Un grup cu un rand legat
 *    de CI / triere il pastreaza pe acela ca principal (Keep se muta numai
 *    intre randurile legate), iar randurile legate nu se unesc niciodata.
 *  - "Clean up old rows": randurile nevazute de N zile, fiecare cu bifa;
 *    cele legate apar dezactivate.
 * Planul il face serverul (`DuplicatePlanner`), si il reface la aplicare: ce
 * nu mai e valid intre timp se sare si se numara.
 */

const STALE_SUGGESTED = 30

type Tab = 'merge' | 'cleanup'

const SCOPES: { value: ConsolidateScope; label: string; help: string }[] = [
  {
    value: 'relay',
    label: 'Same relay',
    help: 'Merge only rows reported by the same relay. Safest, but misses a machine seen by two relays.',
  },
  {
    value: 'office',
    label: 'Relays in the same office',
    help: 'Merge rows from any relay of one office. Example: RELAY-1 and RELAY-2 in the same office both list 172.16.0.4.',
  },
  {
    value: 'company',
    label: 'Whole company',
    help: 'Merge across all offices. Risky: two offices can use the same private IP for two different machines.',
  },
]

/** Pe fiecare grup: randul ales sa ramana si randurile debifate. */
interface Pick {
  keep: number
  excluded: ReadonlySet<number>
}

function groupKey(g: DuplicateGroup): number {
  return g.survivor.id
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`
}

/** Randurile unui grup, in ordinea planului: cel propus sa ramana, cele de unit, cele legate. */
function groupRows(g: DuplicateGroup): DuplicateRow[] {
  return [g.survivor, ...g.remove, ...g.kept]
}

/** Ce s-ar intampla cu grupul dupa alegerea omului. */
function effective(g: DuplicateGroup, pick: Pick | undefined) {
  const rows = groupRows(g)
  const keep = pick?.keep ?? g.survivor.id
  const excluded = pick?.excluded ?? new Set<number>()
  const merged = rows.filter((r) => r.id !== keep && !r.referenced && !excluded.has(r.id))
  return { rows, keep, excluded, merged, hasLinked: rows.some((r) => r.referenced) }
}

function relayLabel(row: DuplicateRow): string {
  return row.relay_name || (row.relay > 0 ? `#${row.relay}` : '-')
}

function officeLabel(row: DuplicateRow, offices: Map<number, string>): string {
  return row.office > 0 ? (offices.get(row.office) ?? `#${row.office}`) : '-'
}

function rowName(row: DuplicateRow): string {
  return `${row.address || `#${row.id}`}${row.relay_name ? ` from ${row.relay_name}` : ''}`
}

/** Celulele comune ale unui rand: Name / IP / MAC / Relay / Office / Last seen. */
function RowCells({ row, offices }: { row: DuplicateRow; offices: Map<number, string> }) {
  return (
    <>
      <td data-label="Name" className="cdm-cell-name">
        {row.name || <span className="text-muted">no name</span>}
      </td>
      <td data-label="IP">{row.address || '-'}</td>
      <td data-label="MAC">{row.mac || <span className="text-muted">no MAC</span>}</td>
      <td data-label="Relay">{relayLabel(row)}</td>
      <td data-label="Office">{officeLabel(row, offices)}</td>
      <td data-label="Last seen" className="text-nowrap">
        {row.last_saw ?? '-'}
      </td>
    </>
  )
}

const NOTE = {
  keeps: <span className="label label-success">Stays</span>,
  removed: <span className="label label-danger">Merged</span>,
  linked: (
    <span className="label label-info" title="Linked to a CI or waiting in triage, so it is never merged or deleted">
      Linked, stays
    </span>
  ),
  out: <span className="label label-default">Left out</span>,
}

/** "Select all | Deselect all" deasupra listei de grupuri / de randuri vechi. */
function BulkLinks({ id, onAll, onNone }: { id: string; onAll(): void; onNone(): void }) {
  return (
    <div className="cdm-bulk" id={id}>
      <button type="button" className="cdm-bulk-link cdm-select-all" onClick={onAll}>
        Select all
      </button>
      <span className="cdm-bulk-sep" aria-hidden="true">
        |
      </span>
      <button type="button" className="cdm-bulk-link cdm-deselect-all" onClick={onNone}>
        Deselect all
      </button>
    </div>
  )
}

function GroupTable({
  group,
  pick,
  on,
  offices,
  onToggle,
  onKeep,
  onInclude,
}: {
  group: DuplicateGroup
  pick: Pick | undefined
  on: boolean
  offices: Map<number, string>
  onToggle(): void
  onKeep(id: number): void
  onInclude(id: number, included: boolean): void
}) {
  const { rows, keep, excluded, merged, hasLinked } = effective(group, pick)
  const key = groupKey(group)
  return (
    <div className={'cdm-group' + (on ? '' : ' is-off')} data-group={key}>
      <label className="cdm-group-pick">
        <input type="checkbox" className="consolidate-pick" checked={on} onChange={onToggle} />
        <strong>{group.merged.device_name || group.survivor.address}</strong>
        <span className="text-muted">{group.survivor.address}</span>
        <span className="cdm-group-count">{plural(rows.length, 'row')}</span>
      </label>
      {hasLinked && (
        <p className="cdm-group-note cdm-group-linked">
          <i className="fa fa-link" aria-hidden="true"></i> A row here is linked to a CI or waiting in triage, so a linked row stays the main one.
        </p>
      )}
      {on && merged.length === 0 && <p className="cdm-group-note cdm-group-skip">Nothing left to merge: this group will be skipped.</p>}
      <div className="cdm-table-wrap">
        <table className="table table-condensed consolidate-group">
          <thead>
            <tr>
              <th scope="col" className="cdm-col-pick" title="The row that stays">
                Keep
              </th>
              <th scope="col" className="cdm-col-pick" title="Merge this row into the one that stays">
                Include
              </th>
              <th scope="col">Name</th>
              <th scope="col">IP</th>
              <th scope="col">MAC</th>
              <th scope="col">Relay</th>
              <th scope="col">Office</th>
              <th scope="col">Last seen</th>
              <th scope="col">Note</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isKeep = r.id === keep
              const isOut = !isKeep && !r.referenced && excluded.has(r.id)
              const tag: keyof typeof NOTE = isKeep ? 'keeps' : r.referenced ? 'linked' : isOut ? 'out' : 'removed'
              return (
                <tr key={r.id} className={'consolidate-row consolidate-' + tag} data-row={r.id}>
                  <td data-label="Keep" className="cdm-col-pick">
                    <input
                      type="radio"
                      className="consolidate-keep"
                      name={`consolidate-keep-${key}`}
                      aria-label={`Keep ${rowName(r)}`}
                      checked={isKeep}
                      disabled={!on || isOut || (hasLinked && !r.referenced)}
                      title={hasLinked && !r.referenced ? 'A linked row stays the main one in this group' : undefined}
                      onChange={() => onKeep(r.id)}
                    />
                  </td>
                  <td data-label="Include" className="cdm-col-pick">
                    <input
                      type="checkbox"
                      className="consolidate-include"
                      aria-label={`Include ${rowName(r)}`}
                      checked={isKeep || (!r.referenced && !isOut)}
                      disabled={!on || isKeep || r.referenced}
                      onChange={(e) => onInclude(r.id, e.target.checked)}
                    />
                  </td>
                  <RowCells row={r} offices={offices} />
                  <td data-label="Note">{NOTE[tag]}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function MergeTab({
  options,
  set,
  preview,
  groups,
  picks,
  unchecked,
  advanced,
  onAdvanced,
  offices,
  totals,
  onToggle,
  onKeep,
  onInclude,
  onAuto,
  onSelectAll,
  onDeselectAll,
}: {
  options: ConsolidateOptions
  set<K extends keyof ConsolidateOptions>(key: K, value: ConsolidateOptions[K]): void
  preview: UseQueryResult<DiscoveredDuplicates>
  groups: DuplicateGroup[]
  picks: Map<number, Pick>
  unchecked: Set<number>
  advanced: boolean
  onAdvanced(): void
  offices: Map<number, string>
  totals: { groups: number; rows: number }
  onToggle(id: number): void
  onKeep(g: DuplicateGroup, id: number): void
  onInclude(g: DuplicateGroup, id: number, included: boolean): void
  onAuto(enabled: boolean): void
  onSelectAll(): void
  onDeselectAll(): void
}) {
  const data = preview.data
  const canApply = data?.can_apply === true
  const proposedRows = groups.reduce((n, g) => n + g.remove.length, 0)
  return (
    <div role="tabpanel" id="consolidatePanelMerge" aria-labelledby="consolidateTabMerge">
      <p className="cdm-intro">
        The same machine can be listed more than once: each relay that sees it without its MAC address adds a row of its own.
        This merges those rows into one. The row that stays keeps the earliest first seen and the latest last seen. Rows
        linked to a CI or waiting in triage are never deleted.
      </p>

      <section className="cdm-section" role="radiogroup" aria-labelledby="consolidateScopeTitle">
        <h4 className="cdm-heading" id="consolidateScopeTitle">
          Relays taken into account
        </h4>
        <div className="cdm-cards">
          {SCOPES.map((s) => (
            <label className={'cdm-option cdm-card' + (options.scope === s.value ? ' is-checked' : '')} key={s.value}>
              <input type="radio" name="consolidate-scope" value={s.value} checked={options.scope === s.value} onChange={() => set('scope', s.value)} />
              <span className="cdm-option-text">
                <span className="cdm-option-label">{s.label}</span>
                <span className="cdm-option-help">{s.help}</span>
              </span>
            </label>
          ))}
        </div>
        {options.scope === 'company' && (
          <div className="alert alert-warning cdm-warning" id="consolidateCompanyWarning">
            <i className="fas fa-exclamation-triangle"></i> The same private address can belong to two different machines in
            two different offices (two sites both using 192.168.1.0/24, for example). Across the whole company, rows on the same
            address are merged even when they are not the same machine.
          </div>
        )}
      </section>

      <section className="cdm-section" aria-labelledby="consolidateRulesTitle">
        <h4 className="cdm-heading" id="consolidateRulesTitle">
          Matching rules
        </h4>
        <div className="cdm-list">
          <label className="cdm-option">
            <input type="checkbox" id="consolidateIntoMac" checked={options.into_mac} onChange={(e) => set('into_mac', e.target.checked)} />
            <span className="cdm-option-text">
              <span className="cdm-option-label">Merge rows without a MAC into the row that has one</span>
              <span className="cdm-option-help">
                Example: 172.16.0.4 is listed 4 times, once with MAC 00:50:56:97:d4:b5 and 3 times without. They become one row.
                Skipped when two different MACs share the same IP.
              </span>
            </span>
          </label>
          <label className="cdm-option">
            <input type="checkbox" id="consolidateSameName" checked={options.same_name} onChange={(e) => set('same_name', e.target.checked)} />
            <span className="cdm-option-text">
              <span className="cdm-option-label">Same name, different IP</span>
              <span className="cdm-option-help">
                Example: a laptop seen as PC-ANA on 172.16.0.20 and later on 172.16.0.35. Merged only if the MAC matches or one row
                has no MAC.
              </span>
            </span>
          </label>
        </div>
      </section>

      <section className="cdm-section" aria-labelledby="consolidateWhereTitle">
        <h4 className="cdm-heading" id="consolidateWhereTitle">
          Office
        </h4>
        <div className="cdm-field">
          <label htmlFor="consolidateOffice" className="cdm-field-label">
            Office
          </label>
          <select id="consolidateOffice" className="form-control cdm-select" value={String(options.office)} onChange={(e) => set('office', Number(e.target.value) || 0)}>
            <option value="0">All offices</option>
            {(data?.offices ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </div>
      </section>

      <div className={'cdm-summary' + (preview.isError ? ' is-error' : '')} id="consolidateSummary" role="status">
        {preview.isPending ? (
          'Looking for duplicates...'
        ) : preview.isError ? (
          'Could not check for duplicates.'
        ) : (
          <>
            <span className="cdm-summary-main">
              <strong>{plural(groups.length, 'group')}</strong> found, <strong>{plural(proposedRows, 'row')}</strong> would be merged away.
            </span>
            {(totals.groups !== groups.length || totals.rows !== proposedRows) && (
              <span className="cdm-summary-note" id="consolidateChosen">
                {' '}
                With your choices: {plural(totals.groups, 'group')}, {plural(totals.rows, 'row')}.
              </span>
            )}
            {(data?.ambiguous ?? 0) > 0 && (
              <span className="cdm-summary-note">
                {' '}
                {plural(data?.ambiguous ?? 0, 'address', 'addresses')} with more than one MAC {data?.ambiguous === 1 ? 'was' : 'were'} left alone.
              </span>
            )}
            {!canApply && <span className="cdm-summary-note"> Only a Super Admin can apply it.</span>}
          </>
        )}
      </div>

      <div className={'cdm-advanced' + (advanced ? ' is-open' : '')}>
        <button
          type="button"
          className="cdm-advanced-toggle"
          id="consolidateAdvanced"
          aria-expanded={advanced}
          aria-controls="consolidateAdvancedBody"
          onClick={onAdvanced}
        >
          <i className="fa fa-chevron-right cdm-chevron" aria-hidden="true"></i>
          <span className="cdm-advanced-title">Advanced</span>
          <span className="cdm-advanced-hint">Pick the row that stays in each group, or leave rows out</span>
        </button>
        {advanced && (
          <div id="consolidateAdvancedBody" className="cdm-advanced-body">
            {groups.length === 0 ? (
              <p className="text-muted cdm-empty">Nothing to consolidate with these options.</p>
            ) : (
              <>
                <BulkLinks id="consolidateBulk" onAll={onSelectAll} onNone={onDeselectAll} />
                <dl className="cdm-legend" id="consolidateLegend">
                  <div>
                    <dt>Keep</dt>
                    <dd>the row that stays. It gets the earliest first seen, the latest last seen, and a name or MAC it is missing.</dd>
                  </div>
                  <div>
                    <dt>Include</dt>
                    <dd>merge this row into the kept one. Uncheck it to leave the row exactly as it is.</dd>
                  </div>
                  <div>
                    <dt>Linked</dt>
                    <dd>linked to a CI or waiting in triage: never merged or deleted, and it stays the main row of its group.</dd>
                  </div>
                </dl>
              </>
            )}
            {groups.map((g) => (
              <GroupTable
                key={groupKey(g)}
                group={g}
                pick={picks.get(groupKey(g))}
                on={!unchecked.has(groupKey(g))}
                offices={offices}
                onToggle={() => onToggle(groupKey(g))}
                onKeep={(id) => onKeep(g, id)}
                onInclude={(id, inc) => onInclude(g, id, inc)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="cdm-auto">
        <label className="cdm-option">
          <input type="checkbox" id="consolidateAuto" checked={data?.auto === true} disabled={!canApply || data === undefined} onChange={(e) => onAuto(e.target.checked)} />
          <span className="cdm-option-text">
            <span className="cdm-option-label">Consolidate automatically after each scan</span>
            <span className="cdm-option-help">Relays in the same office, rows without a MAC into the row that has one. Old rows are never deleted automatically.</span>
          </span>
        </label>
      </div>
    </div>
  )
}

export function ConsolidateDuplicatesModal({ open, onClose, onDone }: { open: boolean; onClose(): void; onDone(): void }) {
  const [tab, setTab] = useState<Tab>('merge')
  const [options, setOptions] = useState<ConsolidateOptions>(CONSOLIDATE_DEFAULTS)
  const [advanced, setAdvanced] = useState(false)
  /** Grupurile debifate, dupa supravietuitorul planului. */
  const [unchecked, setUnchecked] = useState<Set<number>>(new Set())
  /** Alegerile din "Advanced", dupa supravietuitorul planului. */
  const [picks, setPicks] = useState<Map<number, Pick>>(new Map())
  const [oldDays, setOldDays] = useState(STALE_SUGGESTED)
  const [oldOffice, setOldOffice] = useState(0)
  /** Randurile vechi debifate. */
  const [oldOff, setOldOff] = useState<Set<number>>(new Set())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const queryClient = useQueryClient()

  /* Curatenia pe vechime are fila ei: unirea nu mai sterge randuri vechi. */
  const wanted: ConsolidateOptions = { ...options, stale_days: 0 }
  const preview = useQuery({ ...discoveredDuplicatesQuery(wanted), enabled: open })
  const data = preview.data
  const groups = useMemo(() => data?.plan ?? [], [data])
  const offices = useMemo(() => new Map((data?.offices ?? []).map((o) => [o.id, o.name])), [data])

  const days = Math.max(1, Math.min(3650, oldDays || 0))
  const oldPreview = useQuery({ ...discoveredOldRowsQuery({ days, office: oldOffice }), enabled: open && tab === 'cleanup' })
  const oldData = oldPreview.data
  const oldRows = useMemo(() => oldData?.rows ?? [], [oldData])
  const oldOffices = useMemo(() => new Map((oldData?.offices ?? data?.offices ?? []).map((o) => [o.id, o.name])), [oldData, data])

  /* Un plan nou (alte optiuni, alta deschidere) porneste cu propunerea serverului. */
  useEffect(() => {
    setUnchecked(new Set())
    setPicks(new Map())
  }, [data])

  useEffect(() => {
    setOldOff(new Set())
  }, [oldData])

  useEffect(() => {
    if (!open) return
    setTab('merge')
    setOptions(CONSOLIDATE_DEFAULTS)
    setOldDays(STALE_SUGGESTED)
    setOldOffice(0)
    setAdvanced(false)
    setError('')
  }, [open])

  /* Ce pleaca la server: grupurile bifate, cu randul ales si randurile lasate bifate, de la 2 randuri in sus. */
  const chosen = groups
    .filter((g) => !unchecked.has(groupKey(g)))
    .map((g) => {
      const e = effective(g, picks.get(groupKey(g)))
      return { survivor: e.keep, remove: e.merged.map((r) => r.id) }
    })
    .filter((g) => g.remove.length > 0)
  const chosenRows = chosen.reduce((n, g) => n + g.remove.length, 0)
  const deletable = oldRows.filter((r) => !r.referenced)
  const toDelete = deletable.filter((r) => !oldOff.has(r.id))
  const allOld = deletable.length > 0 && toDelete.length === deletable.length

  const canApply = tab === 'merge' ? data?.can_apply === true : oldData?.can_apply === true
  const nothing = tab === 'merge' ? chosen.length === 0 : toDelete.length === 0
  const fetching = tab === 'merge' ? preview.isFetching : oldPreview.isFetching

  const set = <K extends keyof ConsolidateOptions>(key: K, value: ConsolidateOptions[K]) => setOptions((o) => ({ ...o, [key]: value }))

  const flip = (prev: Set<number>, id: number) => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  }

  const choose = (g: DuplicateGroup, change: (p: Pick) => Pick) =>
    setPicks((prev) => {
      const next = new Map(prev)
      next.set(groupKey(g), change(prev.get(groupKey(g)) ?? { keep: g.survivor.id, excluded: new Set<number>() }))
      return next
    })

  const keepRow = (g: DuplicateGroup, id: number) =>
    choose(g, (p) => {
      const excluded = new Set(p.excluded)
      excluded.delete(id)
      return { keep: id, excluded }
    })

  const includeRow = (g: DuplicateGroup, id: number, included: boolean) =>
    choose(g, (p) => {
      const excluded = new Set(p.excluded)
      if (included) excluded.delete(id)
      else excluded.add(id)
      return { keep: p.keep, excluded }
    })

  const fail = (err: unknown) => {
    const forbidden = err instanceof ApiError && err.status === 403
    setError(forbidden ? 'Only a Super Admin can do this.' : 'Could not apply the change. Please try again later.')
  }

  const finish = () => {
    void queryClient.invalidateQueries({ queryKey: ['inventoree', 'discovered', 'duplicates'] })
    void queryClient.invalidateQueries({ queryKey: ['inventoree', 'discovered', 'old-rows'] })
    onDone()
    onClose()
  }

  const applyMerge = () =>
    consolidateDiscoveredDuplicates(wanted, chosen, []).then((res) => {
      const skipped = res.skipped ?? 0
      const skippedRows = res.skipped_rows ?? 0
      const parts = [`${plural(res.groups, 'group')} consolidated, ${plural(res.removed, 'row')} removed.`]
      if (skipped > 0) parts.push(`${plural(skipped, 'group')} changed since the preview and ${skipped === 1 ? 'was' : 'were'} left alone.`)
      if (skippedRows > 0) parts.push(`${plural(skippedRows, 'linked row')} left as ${skippedRows === 1 ? 'it was' : 'they were'}.`)
      toast(parts.join(' '), 'Done')
    })

  const applyCleanup = () =>
    deleteDiscoveredOldRows(
      { days, office: oldOffice },
      toDelete.map((r) => r.id),
    ).then((res) => {
      const skipped = res.skipped ?? 0
      const parts = [`${plural(res.removed ?? 0, 'old row')} deleted.`]
      if (skipped > 0) parts.push(`${plural(skipped, 'row')} changed since the preview and ${skipped === 1 ? 'was' : 'were'} left alone.`)
      toast(parts.join(' '), 'Done')
    })

  const apply = () => {
    if (busy || nothing || !canApply) return
    setBusy(true)
    setError('')
    void (tab === 'merge' ? applyMerge() : applyCleanup())
      .then(finish)
      .catch(fail)
      .finally(() => setBusy(false))
  }

  const toggleAuto = (enabled: boolean) => {
    void setDiscoveredAutoConsolidate(enabled)
      .then(() => {
        toast(enabled ? 'Duplicates will be consolidated after each scan.' : 'Automatic consolidation is off.', 'Saved')
        void queryClient.invalidateQueries({ queryKey: ['inventoree', 'discovered', 'duplicates'] })
      })
      .catch((err: unknown) => toast(err instanceof ApiError ? err.message : 'Could not save the setting.', 'Error', 'error'))
  }

  const buttonLabel =
    tab === 'merge'
      ? `Consolidate ${plural(chosen.length, 'group')}, ${plural(chosenRows, 'row')}`
      : `Delete ${plural(toDelete.length, 'old row')}`

  return (
    <Modal
      id="consolidateDuplicates"
      open={open}
      onClose={onClose}
      large
      portal
      title="Consolidate duplicates"
      footer={
        <>
          <button
            type="button"
            className={'btn ' + (tab === 'merge' ? 'btn-warning' : 'btn-danger')}
            id="consolidateApply"
            disabled={busy || nothing || !canApply || fetching}
            onClick={apply}
          >
            {buttonLabel}
          </button>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      <div className="cdm">
        <div className="cdm-tabs" role="tablist" aria-label="What to do">
          <button
            type="button"
            role="tab"
            id="consolidateTabMerge"
            className={'cdm-tab' + (tab === 'merge' ? ' is-active' : '')}
            aria-selected={tab === 'merge'}
            aria-controls="consolidatePanelMerge"
            onClick={() => setTab('merge')}
          >
            <i className="fa fa-compress" aria-hidden="true"></i> Merge duplicates
          </button>
          <button
            type="button"
            role="tab"
            id="consolidateTabCleanup"
            className={'cdm-tab' + (tab === 'cleanup' ? ' is-active' : '')}
            aria-selected={tab === 'cleanup'}
            aria-controls="consolidatePanelCleanup"
            onClick={() => setTab('cleanup')}
          >
            <i className="fa fa-trash" aria-hidden="true"></i> Clean up old rows
          </button>
        </div>

        {error !== '' && <div className="alert alert-danger">{error}</div>}

        {tab === 'merge' ? (
          <MergeTab
            options={options}
            set={set}
            preview={preview}
            groups={groups}
            picks={picks}
            unchecked={unchecked}
            advanced={advanced}
            onAdvanced={() => setAdvanced((v) => !v)}
            offices={offices}
            totals={{ groups: chosen.length, rows: chosenRows }}
            onToggle={(id) => setUnchecked((prev) => flip(prev, id))}
            onKeep={keepRow}
            onInclude={includeRow}
            onAuto={toggleAuto}
            onSelectAll={() => {
              /* Inapoi la propunerea serverului: toate grupurile, cu randurile lor. */
              setUnchecked(new Set())
              setPicks(new Map())
            }}
            onDeselectAll={() => setUnchecked(new Set(groups.map(groupKey)))}
          />
        ) : (
          <div role="tabpanel" id="consolidatePanelCleanup" aria-labelledby="consolidateTabCleanup">
            <p className="cdm-intro">
              Deletes rows of the Discovered list that no relay has reported for a while: machines that were replaced, renumbered
              or switched off for good. Only the list row goes; nothing in the inventory is touched. Rows linked to a CI or waiting
              in triage are never deleted.
            </p>
            <section className="cdm-section" aria-labelledby="cleanupWhatTitle">
              <h4 className="cdm-heading" id="cleanupWhatTitle">
                Which rows
              </h4>
              <div className="cdm-field">
                <label htmlFor="cleanupDays" className="cdm-field-label">
                  Not seen for more than
                </label>
                <span className="cdm-stale-days">
                  <input
                    type="number"
                    id="cleanupDays"
                    min={1}
                    max={3650}
                    className="form-control input-sm"
                    value={oldDays}
                    onChange={(e) => setOldDays(Number(e.target.value) || 0)}
                  />
                  <span className="cdm-option-label">days</span>
                </span>
              </div>
              <div className="cdm-field">
                <label htmlFor="cleanupOffice" className="cdm-field-label">
                  Office
                </label>
                <select id="cleanupOffice" className="form-control cdm-select" value={String(oldOffice)} onChange={(e) => setOldOffice(Number(e.target.value) || 0)}>
                  <option value="0">All offices</option>
                  {[...oldOffices].map(([id, name]) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            </section>

            <div className={'cdm-summary' + (oldPreview.isError ? ' is-error' : '')} id="cleanupSummary" role="status">
              {oldPreview.isPending ? (
                'Looking for old rows...'
              ) : oldPreview.isError ? (
                'Could not check for old rows.'
              ) : (
                <>
                  <span className="cdm-summary-main">
                    <strong>{plural(oldData?.deletable ?? 0, 'row')}</strong> not seen for more than {plural(oldData?.days ?? days, 'day')}
                    {(oldData?.linked ?? 0) > 0 && (
                      <>
                        , plus <strong>{plural(oldData?.linked ?? 0, 'linked row')}</strong> that {(oldData?.linked ?? 0) === 1 ? 'stays' : 'stay'}
                      </>
                    )}
                    .
                  </span>
                  {toDelete.length !== deletable.length && <span className="cdm-summary-note"> {plural(toDelete.length, 'row')} checked.</span>}
                  {oldData?.can_apply !== true && <span className="cdm-summary-note"> Only a Super Admin can delete them.</span>}
                </>
              )}
            </div>

            {oldRows.length > 0 && (
              <BulkLinks id="cleanupBulk" onAll={() => setOldOff(new Set())} onNone={() => setOldOff(new Set(deletable.map((r) => r.id)))} />
            )}
            {oldRows.length > 0 && (
              <div className="cdm-table-wrap cdm-old-wrap">
                <table className="table table-condensed consolidate-group" id="consolidateOldRows">
                  <thead>
                    <tr>
                      <th scope="col" className="cdm-col-pick">
                        <label className="cdm-th-check">
                          <input
                            type="checkbox"
                            id="cleanupAll"
                            aria-label="Delete all"
                            checked={allOld}
                            disabled={deletable.length === 0}
                            onChange={() => setOldOff(allOld ? new Set(deletable.map((r) => r.id)) : new Set())}
                          />
                          Delete
                        </label>
                      </th>
                      <th scope="col">Name</th>
                      <th scope="col">IP</th>
                      <th scope="col">MAC</th>
                      <th scope="col">Relay</th>
                      <th scope="col">Office</th>
                      <th scope="col">Last seen</th>
                      <th scope="col">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {oldRows.map((r) => {
                      const on = !r.referenced && !oldOff.has(r.id)
                      const tag = r.referenced ? 'linked' : on ? 'old' : 'out'
                      return (
                        <tr key={r.id} className={'consolidate-row consolidate-' + tag} data-row={r.id}>
                          <td data-label="Delete" className="cdm-col-pick">
                            <input
                              type="checkbox"
                              className="cleanup-pick"
                              aria-label={`Delete ${rowName(r)}`}
                              checked={on}
                              disabled={r.referenced}
                              title={r.referenced ? 'Linked to a CI or waiting in triage, so it is never deleted' : undefined}
                              onChange={() => setOldOff((prev) => flip(prev, r.id))}
                            />
                          </td>
                          <RowCells row={r} offices={oldOffices} />
                          <td data-label="Note">
                            {r.referenced ? (
                              <span className="label label-info" title="Linked to a CI or waiting in triage, so it is never deleted">
                                Linked, never deleted
                              </span>
                            ) : on ? (
                              <span className="label label-danger">Deleted</span>
                            ) : (
                              <span className="label label-default">Left out</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {oldData !== undefined && oldRows.length === 0 && <p className="text-muted cdm-empty">No row is that old.</p>}
          </div>
        )}
      </div>
    </Modal>
  )
}
